import { lerRespostasGuardadas, humanizarPergunta } from './meta-lead-ads.js';
import { normalizarTelefone } from './normalizacao.js';
/**
 * O Lead da Meta como o B2Cor o recebe.
 *
 * O B2Cor é o CRM que a Comercial já usa fora do Sales Flow. Esta camada só
 * traduz: o que ele aceita está em `POST /lead/add/:modelo/:origem`, e o que
 * sai daqui é o mínimo para a pessoa existir lá e para quem a atender saber de
 * onde ela veio e o que respondeu.
 */
/**
 * Quantas tentativas o envio faz antes de desistir e ficar FALHOU.
 *
 * Alto de propósito: com a espera de uma hora no teto, são cerca de oito dias.
 * Só desiste quem o B2Cor recusou, ou quem ele não aceitou durante uma semana
 * — B2Cor fora do ar ou chave trocada não podem gastar as tentativas de Leads
 * que não têm culpa de nada e marcá-los como perdidos.
 */
export const MAXIMO_DE_TENTATIVAS_DO_ENVIO_AO_B2COR = 200;
/** Teto do texto que vai para o histórico do lead no B2Cor. */
const MAXIMO_CARACTERES_DO_HISTORICO = 4000;
/**
 * Telefone no formato do exemplo do B2Cor: DDD, espaço e o número.
 *
 * Nulo quando não há DDD e número completos: o B2Cor recusa telefone que não
 * consegue separar, e mandar lixo trocaria um Lead sem telefone por um Lead
 * recusado inteiro.
 */
export function formatarTelefoneParaOB2cor(telefone) {
    const digitos = normalizarTelefone(telefone);
    if (!digitos)
        return null;
    return `${digitos.slice(0, 2)} ${digitos.slice(2)}`;
}
const FORMATO_DA_DATA = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});
/**
 * Texto do histórico: de onde o Lead veio e o que a pessoa respondeu.
 *
 * As respostas vão porque é com elas que a Comercial abre a conversa — plano
 * atual, quanto paga, quantas vidas. São dado de saúde e renda, e esta função
 * é o único lugar que decide mandá-las a um terceiro: mudar essa decisão é
 * mudar aqui, e só aqui.
 */
export function montarHistoricoParaOB2cor(lead) {
    const linhas = [
        'Lead do Facebook Ads (formulário Lead Ads da Meta), recebido pelo Sales Flow.',
        `Preencheu o formulário em ${FORMATO_DA_DATA.format(lead.entrouEm)}.`,
    ];
    if (lead.campanhaDeAnunciosNome) {
        linhas.push(`Campanha de anúncios: ${lead.campanhaDeAnunciosNome}`);
    }
    if (lead.anuncioNome)
        linhas.push(`Anúncio: ${lead.anuncioNome}`);
    const respostas = lerRespostasGuardadas(lead.respostasDoFormulario);
    if (respostas.length > 0) {
        linhas.push('', 'Respostas do formulário:');
        for (const { pergunta, respostas: dadas } of respostas) {
            const resposta = dadas.map(humanizarPergunta).filter((texto) => texto !== '').join(', ');
            linhas.push(`- ${humanizarPergunta(pergunta)}: ${resposta === '' ? '(sem resposta)' : resposta}`);
        }
    }
    const texto = linhas.join('\n');
    return texto.length > MAXIMO_CARACTERES_DO_HISTORICO
        ? `${texto.slice(0, MAXIMO_CARACTERES_DO_HISTORICO - 1)}…`
        : texto;
}
export function montarLeadParaOB2cor(lead, origemId) {
    const fone = formatarTelefoneParaOB2cor(lead.telefone);
    const email = lead.email?.trim() ?? '';
    return {
        nome: lead.nome.trim(),
        ...(email === '' ? {} : { email }),
        ...(fone === null ? {} : { fone }),
        info: montarHistoricoParaOB2cor(lead),
        origem: origemId,
    };
}
/**
 * Lê só o status HTTP, e nunca o corpo: a doc do B2Cor diz que, fora a
 * listagem, nenhum método devolve dado, e que o resultado se lê pelo status.
 * O motivo é texto nosso, pelo mesmo cuidado que a Meta recebeu — um corpo de
 * erro que devolvesse o lead recebido levaria nome e telefone para o log.
 */
export function classificarRespostaDoB2cor(status) {
    if (status >= 200 && status < 300)
        return { enviado: true };
    if (status === 401 || status === 403) {
        return {
            enviado: false,
            classe: 'CHAVE_RECUSADA',
            definitiva: false,
            motivo: `o B2Cor recusou a chave da API (${status}): ela foi trocada ou revogada. ` +
                'O Lead segue pendente e sai quando a chave for corrigida',
        };
    }
    if (status === 400 || status === 422) {
        return {
            enviado: false,
            classe: 'LEAD_RECUSADO',
            definitiva: true,
            motivo: `o B2Cor recusou os dados do Lead (${status}): tentar de novo não muda a resposta`,
        };
    }
    if (status === 404 || status === 405) {
        return {
            enviado: false,
            classe: 'ENDERECO_RECUSADO',
            definitiva: false,
            motivo: `o B2Cor não reconheceu a chamada (${status}): é configuração nossa, ` +
                'e o Lead segue pendente até ela ser corrigida',
        };
    }
    return {
        enviado: false,
        classe: 'INDISPONIVEL',
        definitiva: false,
        motivo: `o B2Cor respondeu ${status}: é transitório, vale tentar de novo`,
    };
}
export function montarColocacaoNoFunil(b2corId, destino) {
    return {
        leads: [{ lead: Number(b2corId), funnel: Number(destino.funilId), stage: Number(destino.etapaId) }],
    };
}
/**
 * Funil em que o lead está, lido da resposta de `GET /lead/get/:id`.
 *
 * Nulo quando ele não está em nenhum. É o que decide se se coloca ou não:
 * repetir a colocação com outra etapa **move o card** (medido em 07/10/2026),
 * então um lead que a Comercial já arrastou, ou que já estava no funil quando o
 * B2Cor reconheceu o repetido, não pode ser tocado de novo.
 *
 * A doc diz `{ lead: [...] }`, mas a resposta real chega como a lista direta, e
 * `funnel` vem como texto vazio para quem não tem funil. Aceita os dois
 * formatos. Quando a forma não é reconhecida devolve `indeterminado`, e quem
 * chama não deve colocar: sem saber, mexer é pior que esperar.
 */
export function funilDoLeadNoB2cor(corpo) {
    const lista = Array.isArray(corpo)
        ? corpo
        : typeof corpo === 'object' && corpo !== null && Array.isArray(corpo.lead)
            ? corpo.lead
            : null;
    const lead = lista?.[0];
    if (typeof lead !== 'object' || lead === null || !('funnel' in lead)) {
        return { estado: 'indeterminado' };
    }
    const funil = lead.funnel;
    if (funil === null || funil === '' || funil === 0 || funil === '0')
        return { estado: 'sem-funil' };
    if (typeof funil === 'string' || typeof funil === 'number') {
        return { estado: 'em-funil', funilId: String(funil) };
    }
    return { estado: 'indeterminado' };
}
/**
 * A colocação no funil nunca é culpa do lead: o que o B2Cor recusa aqui é o
 * funil ou a etapa, que são configuração nossa. Por isso nada é definitivo — o
 * lead já existe lá, e fica esperando o número ser corrigido.
 */
export function classificarRespostaDaColocacao(status) {
    if (status >= 200 && status < 300)
        return { enviado: true };
    if (status === 401 || status === 403) {
        return {
            enviado: false,
            classe: 'CHAVE_RECUSADA',
            definitiva: false,
            motivo: `o B2Cor recusou a chave da API ao colocar no funil (${status}). ` +
                'O lead já está lá e segue esperando a colocação',
        };
    }
    if (status >= 400 && status < 500) {
        return {
            enviado: false,
            classe: 'ENDERECO_RECUSADO',
            definitiva: false,
            motivo: `o B2Cor recusou a colocação no funil (${status}): confira o funil e a etapa ` +
                'configurados. O lead já está lá e segue esperando a colocação',
        };
    }
    return {
        enviado: false,
        classe: 'INDISPONIVEL',
        definitiva: false,
        motivo: `o B2Cor respondeu ${status} ao colocar no funil: é transitório, vale tentar de novo`,
    };
}
/**
 * Espera até a próxima tentativa, em minutos: 2, 4, 8, 16, 32 e daí 60.
 *
 * Cresce porque B2Cor fora do ar por uma hora não melhora com uma chamada por
 * dois minutos, e para no teto de uma hora porque o Lead não pode ficar mais
 * que isso esperando depois que o problema acaba.
 */
export function minutosAteANovaTentativaAoB2cor(tentativasFeitas) {
    const atual = Math.max(1, Math.floor(tentativasFeitas));
    return Math.min(60, 2 ** Math.min(atual, 6));
}
//# sourceMappingURL=b2cor.js.map